import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, IeAnexo, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { FetchAPI, FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeSubmit, OnCancel, OnClose, OnEdit, OnMount, OnSelect } from '@philips-emr/tws-core/lib/client';
import { AbortableSubmitEvent, MountEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isContentfulString, isEmptyObject, isObject } from '@philips-emr/tws-utils';

import { AlterarSubestipulanteAliases as Aliases, FragmentNames } from '../codegen/constants';
import { Anexo } from '../interfaces/anexo';

import BaseController from './BaseController';

interface Retorno {
  body: Record<string, Value>;
  nrSeqAnexo: number;
  filepath: string;
  filename: string;
}

@Controller({ fragment: FragmentNames.ALTERAR_SUBESTIPULANTE })
export default class AlterarSubestipulanteController extends BaseController {

  private regraAnexos: Anexo[];

  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastForm: ToastNotificationAPI;
  @Inject(Aliases.FORM_ALTERAR_SUBESTIPULANTE)
  private formAlterarSubestipulante: FormAPI;
  @Inject(Aliases.FIELD_SUBESTIPULANTE)
  private fieldSubestipulante: FieldAPI;
  @Inject(Aliases.FILE_UPLOADER)
  private fileUploaderAPI: FileUploaderAPI;
  @Inject(Aliases.FETCH_LISTA_ANEXOS)
  private fetchListaAnexos: FetchAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  private retorno: boolean;

  @OnMount(Aliases.FORM_ALTERAR_SUBESTIPULANTE)
  onMountForm(event: MountEvent): void {
    const apiForm = event.target as unknown as FormAPI;
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Record<string, Value>>(Endpoint.GET.SOLIC_ALT_SUBESTIPULANTE_ABERTA, { nrSeqBeneficiario, nrSeqContrato })
      .then(response => {
        const { body: solicAberta } = response as unknown as Retorno;

        this.getPredefinedFiles();

        const { nrSeqSolicAlt } = solicAberta;
        this.context.set({ nrSeqSolicAlt });
        apiForm.setData(solicAberta);

        if (nrSeqSolicAlt) {
          this.retorno = true;
          this.context.set({ solicAberta });
          this.getListaAnexos();
        } else {
          apiForm.setEditing(true);
        }

        if (!nrSeqSolicAlt ) {
          this.fieldSubestipulante.setState({ showValidations: false });
        }
      });
  }

  @OnBeforeSubmit(Aliases.FORM_ALTERAR_SUBESTIPULANTE)
  onSubmit(event: AbortableSubmitEvent): void {
    this.panelLoader.setVisible(true);
    const dados = this.formAlterarSubestipulante.getRecord();
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const { nrSeqSolicAlt } = this.context.getBulkData();

    dados.anexos = this.handleAnexos(dados.anexos as Anexo[]);

    const body = { ...dados, nrSeqBeneficiario, nrSeqSolicAlt };

    HttpService.post(Endpoint.POST.SOLIC_ALT_SUBESTIPULANTE, { nrSeqBeneficiario, nrSeqContrato }, { body })
      .then(() => {
        this.panelLoader.setVisible(false);
        ToastServiceStipulator.success(MessageService.translate('stipuplator.alteracao.solicitada'));
        this.formAlterarSubestipulante.setEditing(false);
        this.getListaAnexos();
        return false;
      }).catch((response) => {
        this.getPredefinedFiles();
        this.panelLoader.setVisible(false);
        event.abort();
        const { body } = response;
        const openToast = (msg: string|undefined): void => {
          if (msg) {
            ToastServiceStipulator.warning(msg);
          } else {
            this.toastForm.setVisible(true);
          }
          this.formAlterarSubestipulante.setDisabled(false);
        };

        if (Array.isArray(body)) {
          this.formAlterarSubestipulante.setEditing(true);
          const data = body as Anexo[];
          const message = `${MessageService.translate('stipulator.obrigatorio-anexar-arquivo')} ${data.map(anexo => anexo.dsLabelCampo).join(', ')}.`;
          return openToast(message);
        }

        if (isObject(body) && !isEmptyObject(body)) {
          const entries = Object.entries(body);
          const message = entries?.shift()?.[1] ?? '';
          return openToast(message);
        }

        if (isContentfulString(body)) {
          return openToast(body);
        }
      });
  }

  @OnEdit(Aliases.FORM_ALTERAR_SUBESTIPULANTE)
  onEdit(): void {
    this.getPredefinedFiles();
    this.retorno = true;
    this.panelLoader.setVisible(false);
  }

  @OnCancel(Aliases.FORM_ALTERAR_SUBESTIPULANTE)
  onCancelForm(): void {
    if (!this.retorno) {
      const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
      this.historyService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato });
      return;
    }
    this.getListaAnexos();
    this.retorno = false;
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.toastForm.setVisible(false);
  }

  @OnMount(Aliases.FETCH_LISTA_ANEXOS)
  onMountPanelList(): void {
    this.getListaAnexos();
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Record<string, string>>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value as unknown as Retorno;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.SEG_SOLIC_ALT_ANEXO;

    FileService.download(Endpoint.GET.ALTERACAO_SUBESTIPULANTE_ANEXO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  private getPredefinedFiles(): void {
    void HttpService.get(Endpoint.GET.CHECK_TIPO_ANEXOS, { ieFuncionalidade: '20' })
      .then(response => {
        this.fieldUpload.setVisible(true);
        const anexos = response.body as Anexo[];
        const predefinedFiles = anexos.map(regras => {
          const { dsLabelCampo: value, nrSeqRegraAnexo: key } = regras;
          return { key: String(key), value };
        });
        this.regraAnexos = anexos;
        catchRenderException(() => this.fileUploaderAPI.setPredefinedFiles(predefinedFiles));
        if (predefinedFiles.length == 0) {
          this.fieldUpload.setVisible(false);
        }
      });
  }

  private getListaAnexos(): void {
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Record<string, Value>>(Endpoint.GET.SOLIC_ALT_SUBESTIPULANTE_ABERTA, { nrSeqBeneficiario, nrSeqContrato }).then(response => {
      const { body: solicAberta } = response;
      if (solicAberta) {
        const { nrSeqSolicAlt } = solicAberta;
        this.fetchListaAnexos.setParameters({ nrSeqBeneficiario, nrSeqSolicAlt });
        this.fetchListaAnexos.forceFetch();
        this.fieldAnexos.setVisible(true);
        return;
      }
    });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}
